import { sendConsentGatedAnalyticsEvent } from "@/scripts/GoogleAnalytics";
import { trackingService } from "@/services/TrackingService";
import type { FunnelTypes } from "@/types/funnelTypes";
import {REPUTECH_DOMAIN} from "@/utils/constants/brand";

export const CHECKOUT_FUNNEL_EVENT_NAMES = [
  "business_analysis_completed",
  "reviews_viewed",
  "review_selected",
  "contact_details_started",
  "contact_details_completed",
  "begin_checkout",
  "payment_started",
  "checkout_error",
] as const;

export type CheckoutFunnelEventName =
  (typeof CHECKOUT_FUNNEL_EVENT_NAMES)[number];

export const CHECKOUT_ERROR_CATEGORIES = [
  "validation_error",
  "order_submission_failed",
  "payment_initialization_failed",
  "network_error",
  "unknown_error",
] as const;

export type CheckoutErrorCategory = (typeof CHECKOUT_ERROR_CATEGORIES)[number];

const isCheckoutErrorCategory = (
  value: unknown,
): value is CheckoutErrorCategory =>
  CHECKOUT_ERROR_CATEGORIES.some((category) => category === value);

export type CheckoutFunnelEventContext = {
  platform: FunnelTypes;
  selectedReviewCount?: number;
  quotedTotal?: number;
  currency?: string;
  pricingVariant?: string | null;
  experimentVariant?: string | null;
  errorCategory?: CheckoutErrorCategory;
  dedupeKey?: string;
};

const EVENT_DEDUPE_PREFIX = "reputech:funnel-event:";

const sanitizeVariant = (value: string | null | undefined) => {
  if (!value) return undefined;

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

  return normalized || undefined;
};

const sanitizeSessionId = (value: string | null | undefined) => {
  if (!value) return undefined;
  return /^[a-zA-Z0-9_-]{8,100}$/.test(value) ? value : undefined;
};

const getDedupeStorageKey = (
  eventName: CheckoutFunnelEventName,
  context: CheckoutFunnelEventContext,
) => {
  const sessionId = sanitizeSessionId(trackingService?.data.session_id);
  const eventKey = sanitizeVariant(context.dedupeKey) ?? eventName;
  return `${EVENT_DEDUPE_PREFIX}${sessionId ?? "browser"}:${context.platform}:${eventName}:${eventKey}`;
};

const hasAlreadySent = (storageKey: string): boolean => {
  try {
    return window.sessionStorage.getItem(storageKey) === "1";
  } catch {
    return false;
  }
};

const rememberSent = (storageKey: string): void => {
  try {
    window.sessionStorage.setItem(storageKey, "1");
  } catch {
    // Storage restrictions must not break checkout.
  }
};

export const buildCheckoutFunnelEventPayload = (
  eventName: CheckoutFunnelEventName,
  context: CheckoutFunnelEventContext,
  targetWindow: Pick<Window, "location"> | null = typeof window === "undefined"
    ? null
    : window,
): Record<string, string | number> => {
  const sessionId = sanitizeSessionId(trackingService?.data.session_id);
  const experimentVariant = sanitizeVariant(
    context.experimentVariant,
  );
  const pricingVariant = sanitizeVariant(context.pricingVariant);
  const selectedReviewCount = Number.isFinite(context.selectedReviewCount)
    ? Math.max(0, Math.floor(context.selectedReviewCount ?? 0))
    : undefined;
  const quotedTotal = Number.isFinite(context.quotedTotal)
    ? Math.max(0, Number((context.quotedTotal ?? 0).toFixed(2)))
    : undefined;
  const currency = /^[A-Z]{3}$/.test(context.currency ?? "")
    ? context.currency
    : undefined;

  return {
    platform: context.platform,
    ...(sessionId ? { session_id: sessionId } : {}),
    frontend_domain: REPUTECH_DOMAIN,
    ...(experimentVariant ? { experiment_variant: experimentVariant } : {}),
    ...(pricingVariant ? { pricing_variant: pricingVariant } : {}),
    ...(selectedReviewCount !== undefined
      ? { selected_review_count: selectedReviewCount }
      : {}),
    ...(quotedTotal !== undefined ? { quoted_total: quotedTotal } : {}),
    ...(currency ? { currency } : {}),
    ...(eventName === "checkout_error" &&
    isCheckoutErrorCategory(context.errorCategory)
      ? { error_category: context.errorCategory }
      : {}),
  };
};

export const trackCheckoutFunnelEvent = (
  eventName: CheckoutFunnelEventName,
  context: CheckoutFunnelEventContext,
): boolean => {
  if (typeof window === "undefined") return false;
  if (
    eventName === "checkout_error" &&
    !isCheckoutErrorCategory(context.errorCategory)
  ) {
    return false;
  }

  const storageKey = getDedupeStorageKey(eventName, context);
  if (hasAlreadySent(storageKey)) return false;

  const result = sendConsentGatedAnalyticsEvent(
    eventName,
    buildCheckoutFunnelEventPayload(eventName, context),
  );

  if (result === "pushed" || result === "queued") {
    rememberSent(storageKey);
    return true;
  }

  return false;
};
